import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/kitchen';

const C = {
  accent:'#3AAF58', accentLight:'#EAF7EE',
  bg:'#F3F6F3', surface:'#FFFFFF',
  border:'#E2E8E2', border2:'#CDD8CD',
  text:'#1A2E1E', text2:'#3D5E44', text3:'#7A9A80',
  amber:'#D4840F', amberBg:'#FEF6E7', amberBorder:'#F0D499',
  red:'#C83333', redBg:'#FDEAEA', redBorder:'#F0B8B8',
  blue:'#2563A8', blueBg:'#EAF1FB', blueBorder:'#B8D0F0',
};

const VENDOR_CATEGORIES = ['F&B Supplies','Vegetables & Fruits','Dairy','Meat & Poultry','Beverages','Dry Goods','Housekeeping','Laundry','Construction','Electrical','Plumbing','IT & Equipment','Stationery','Horticulture','General'];
const PAYMENT_TERMS = ['Immediate','Credit 7 Days','Credit 15 Days','Credit 30 Days','Credit 45 Days','Credit 60 Days','Advance 50%','Full Advance'];
const inr = n => '₹' + Number(Math.round(n)||0).toLocaleString('en-IN');

const inp = {
  width:'100%', padding:'9px 12px', background:'#F3F6F3',
  border:'1.5px solid #CDD8CD', borderRadius:8,
  color:'#1A2E1E', fontSize:13, boxSizing:'border-box',
};
const lbl = (t) => <div style={{fontSize:11,fontWeight:700,color:'#7A9A80',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:5}}>{t}</div>;

const initForm = {
  name:'', contact_person:'', phone:'', whatsapp:'', email:'',
  address:'', city:'', state:'', pincode:'',
  gst_number:'', pan_number:'',
  category:'F&B Supplies', sub_categories:'',
  payment_terms:'Credit 30 Days', credit_days:30,
  advance_required:false, advance_pct:0,
  bank_name:'', bank_account:'', bank_ifsc:'',
  rating:5, status:'Active', notes:'',
};

export default function VendorManagement() {
  const [vendors, setVendors] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [viewVendor, setViewVendor] = useState(null);
  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [activeTab, setActiveTab] = useState('vendors');
  const [generating, setGenerating] = useState(false);
  const [generatedPOs, setGeneratedPOs] = useState([]);

  // Add item to vendor state
  const [showAddItem, setShowAddItem] = useState(false);
  const [itemForm, setItemForm] = useState({ inventory_item_id:'', item_name:'', unit:'kg', unit_price:'', min_order_qty:1, lead_time_days:1, is_primary:false });

  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const showMsg = (m) => { setMsg(m); setTimeout(()=>setMsg(''),4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, iRes] = await Promise.all([
        fetch(`${API}/vendors`).then(r=>r.json()),
        fetch('https://api.botanix.in/api/inventory').then(r=>r.json()),
      ]);
      setVendors(Array.isArray(vRes)?vRes:[]);
      setInventory(Array.isArray(iRes)?iRes:[]);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(()=>{ load(); },[load]);

  const saveVendor = async () => {
    if (!form.name) { showMsg('Vendor name required'); return; }
    setSaving(true);
    try {
      const url = editVendor ? `${API}/vendors/${editVendor.id}` : `${API}/vendors`;
      const method = editVendor ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
      if (r.ok) {
        setShowAdd(false); setEditVendor(null); setForm(initForm);
        load(); showMsg(editVendor?'Vendor updated!':'Vendor added!');
      } else { const d=await r.json(); showMsg(d.error||'Failed'); }
    } catch(e) { showMsg('Error'); }
    setSaving(false);
  };

  const deleteVendor = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    await fetch(`${API}/vendors/${id}`, {method:'DELETE'});
    load(); showMsg('Vendor deleted');
  };

  const openEdit = (v) => { setForm({...initForm,...v}); setEditVendor(v); setShowAdd(true); };

  const openView = async (v) => {
    const r = await fetch(`${API}/vendors/${v.id}`);
    const d = await r.json();
    setViewVendor(d);
  };

  const addItemToVendor = async () => {
    if (!viewVendor || !itemForm.item_name) return;
    setSaving(true);
    try {
      const selectedInvItem = inventory.find(i=>i.id===parseInt(itemForm.inventory_item_id));
      const payload = {
        ...itemForm,
        vendor_name: viewVendor.name,
        vendor_phone: viewVendor.phone,
        vendor_email: viewVendor.email,
        item_name: selectedInvItem?.item_name || itemForm.item_name,
        unit: selectedInvItem?.unit || itemForm.unit,
      };
      const r = await fetch(`${API}/vendors/${viewVendor.id}/items`, {
        method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload)
      });
      if (r.ok) {
        setShowAddItem(false);
        setItemForm({ inventory_item_id:'', item_name:'', unit:'kg', unit_price:'', min_order_qty:1, lead_time_days:1, is_primary:false });
        openView(viewVendor);
        showMsg('Item linked to vendor!');
      } else showMsg('Failed');
    } catch(e) { showMsg('Error'); }
    setSaving(false);
  };

  const removeVendorItem = async (itemId) => {
    await fetch(`${API}/vendors/${viewVendor.id}/items/${itemId}`, {method:'DELETE'});
    openView(viewVendor);
  };

  const autoGenerateVendorPOs = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${API}/purchase-orders/auto-generate-vendor`, {method:'POST', headers:{'Content-Type':'application/json'}});
      const d = await r.json();
      if (r.ok && d.pos?.length > 0) {
        setGeneratedPOs(d.pos);
        setActiveTab('pos');
        showMsg(`${d.totalPOs} PO(s) generated — one per vendor!`);
      } else showMsg(d.message || 'No low stock items found');
    } catch(e) { showMsg('Error'); }
    setGenerating(false);
  };

  const sendPO = async (poId, via) => {
    const r = await fetch(`${API}/purchase-orders/${poId}/send`, {
      method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({via})
    });
    if (r.ok) showMsg(`PO sent via ${via}!`);
  };

  const filtered = vendors.filter(v => {
    const matchSearch = !search || v.name?.toLowerCase().includes(search.toLowerCase()) || v.contact_person?.toLowerCase().includes(search.toLowerCase());
    const matchCat = !filterCat || v.category === filterCat;
    return matchSearch && matchCat;
  });

  const StarRating = ({rating}) => (
    <div style={{display:'flex',gap:2}}>
      {[1,2,3,4,5].map(s=>(
        <span key={s} style={{color:s<=rating?C.amber:C.border,fontSize:14}}>★</span>
      ))}
    </div>
  );

  const VendorForm = () => (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16,overflowY:'auto'}}>
      <div style={{background:C.surface,borderRadius:16,width:'100%',maxWidth:680,maxHeight:'90vh',overflowY:'auto',padding:28}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <div style={{fontSize:17,fontWeight:800,color:C.text}}>{editVendor?'Edit Vendor':'Add New Vendor'}</div>
          <button onClick={()=>{setShowAdd(false);setEditVendor(null);setForm(initForm);}} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 12px',cursor:'pointer',color:C.text2}}>Cancel</button>
        </div>

        {/* Basic Info */}
        <div style={{fontSize:12,fontWeight:700,color:C.text2,marginBottom:10,textTransform:'uppercase'}}>Basic Information</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
          <div style={{gridColumn:'span 2'}}>{lbl('Vendor/Company Name *')}<input style={inp} value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. Fresh Farm Supplies Pvt Ltd" /></div>
          <div>{lbl('Contact Person')}<input style={inp} value={form.contact_person} onChange={e=>set('contact_person',e.target.value)} placeholder="Primary contact name" /></div>
          <div>{lbl('Category')}<select style={inp} value={form.category} onChange={e=>set('category',e.target.value)}>{VENDOR_CATEGORIES.map(c=><option key={c}>{c}</option>)}</select></div>
          <div>{lbl('Phone')}<input style={inp} value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="Mobile number" /></div>
          <div>{lbl('WhatsApp')}<input style={inp} value={form.whatsapp} onChange={e=>set('whatsapp',e.target.value)} placeholder="WhatsApp number" /></div>
          <div style={{gridColumn:'span 2'}}>{lbl('Email')}<input style={inp} type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="vendor@email.com" /></div>
        </div>

        {/* Address */}
        <div style={{fontSize:12,fontWeight:700,color:C.text2,marginBottom:10,textTransform:'uppercase'}}>Address</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:14}}>
          <div style={{gridColumn:'span 3'}}>{lbl('Address')}<input style={inp} value={form.address} onChange={e=>set('address',e.target.value)} placeholder="Street address" /></div>
          <div>{lbl('City')}<input style={inp} value={form.city} onChange={e=>set('city',e.target.value)} /></div>
          <div>{lbl('State')}<input style={inp} value={form.state} onChange={e=>set('state',e.target.value)} /></div>
          <div>{lbl('Pincode')}<input style={inp} value={form.pincode} onChange={e=>set('pincode',e.target.value)} /></div>
        </div>

        {/* Tax Info */}
        <div style={{fontSize:12,fontWeight:700,color:C.text2,marginBottom:10,textTransform:'uppercase'}}>Tax & Legal</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
          <div>{lbl('GST Number')}<input style={inp} value={form.gst_number} onChange={e=>set('gst_number',e.target.value)} placeholder="GST registration number" /></div>
          <div>{lbl('PAN Number')}<input style={inp} value={form.pan_number} onChange={e=>set('pan_number',e.target.value)} placeholder="PAN number" /></div>
        </div>

        {/* Payment Terms */}
        <div style={{fontSize:12,fontWeight:700,color:C.text2,marginBottom:10,textTransform:'uppercase'}}>Payment Terms</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:14}}>
          <div>{lbl('Payment Terms')}<select style={inp} value={form.payment_terms} onChange={e=>set('payment_terms',e.target.value)}>{PAYMENT_TERMS.map(p=><option key={p}>{p}</option>)}</select></div>
          <div>{lbl('Credit Days')}<input style={inp} type="number" value={form.credit_days} onChange={e=>set('credit_days',e.target.value)} /></div>
          <div>{lbl('Advance %')}<input style={inp} type="number" value={form.advance_pct} onChange={e=>set('advance_pct',e.target.value)} /></div>
        </div>
        <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:14,cursor:'pointer'}}>
          <input type="checkbox" checked={form.advance_required} onChange={e=>set('advance_required',e.target.checked)} />
          <span style={{fontSize:13,color:C.text2,fontWeight:600}}>Advance payment required</span>
        </label>

        {/* Bank Details */}
        <div style={{fontSize:12,fontWeight:700,color:C.text2,marginBottom:10,textTransform:'uppercase'}}>Bank Details</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:14}}>
          <div>{lbl('Bank Name')}<input style={inp} value={form.bank_name} onChange={e=>set('bank_name',e.target.value)} /></div>
          <div>{lbl('Account Number')}<input style={inp} value={form.bank_account} onChange={e=>set('bank_account',e.target.value)} /></div>
          <div>{lbl('IFSC Code')}<input style={inp} value={form.bank_ifsc} onChange={e=>set('bank_ifsc',e.target.value)} /></div>
        </div>

        <div style={{marginBottom:14}}>{lbl('Notes')}<textarea style={{...inp,height:60,resize:'vertical'}} value={form.notes} onChange={e=>set('notes',e.target.value)} /></div>

        <button onClick={saveVendor} disabled={saving}
          style={{width:'100%',padding:'13px',background:C.accent,color:'#fff',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',opacity:saving?0.6:1}}>
          {saving?'Saving...':editVendor?'Update Vendor':'Add Vendor'}
        </button>
      </div>
    </div>
  );

  const VendorDetail = () => {
    if (!viewVendor) return null;
    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
        <div style={{background:C.surface,borderRadius:16,width:'100%',maxWidth:700,maxHeight:'90vh',overflowY:'auto',padding:28}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
            <div>
              <div style={{fontSize:18,fontWeight:800,color:C.text}}>{viewVendor.name}</div>
              <div style={{fontSize:12,color:C.text3,marginTop:2}}>{viewVendor.vendor_code} · {viewVendor.category}</div>
              <StarRating rating={viewVendor.rating||5} />
            </div>
            <button onClick={()=>setViewVendor(null)} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 12px',cursor:'pointer',color:C.text2}}>Close</button>
          </div>

          {/* Vendor Info */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:20,padding:16,background:C.bg,borderRadius:10}}>
            <div><span style={{fontSize:11,color:C.text3}}>Contact</span><div style={{fontSize:13,fontWeight:600,color:C.text}}>{viewVendor.contact_person||'—'}</div></div>
            <div><span style={{fontSize:11,color:C.text3}}>Phone</span><div style={{fontSize:13,fontWeight:600,color:C.text}}>{viewVendor.phone||'—'}</div></div>
            <div><span style={{fontSize:11,color:C.text3}}>Email</span><div style={{fontSize:13,fontWeight:600,color:C.text}}>{viewVendor.email||'—'}</div></div>
            <div><span style={{fontSize:11,color:C.text3}}>Payment Terms</span><div style={{fontSize:13,fontWeight:600,color:C.text}}>{viewVendor.payment_terms}</div></div>
            <div><span style={{fontSize:11,color:C.text3}}>GST</span><div style={{fontSize:13,fontWeight:600,color:C.text}}>{viewVendor.gst_number||'—'}</div></div>
            <div><span style={{fontSize:11,color:C.text3}}>City</span><div style={{fontSize:13,fontWeight:600,color:C.text}}>{viewVendor.city||'—'}</div></div>
          </div>

          {/* Linked Items */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <div style={{fontSize:13,fontWeight:700,color:C.text2,textTransform:'uppercase'}}>Linked Inventory Items ({(viewVendor.items||[]).length})</div>
            <button onClick={()=>setShowAddItem(true)}
              style={{padding:'6px 14px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>
              + Link Item
            </button>
          </div>

          {(viewVendor.items||[]).length===0 ? (
            <div style={{textAlign:'center',padding:20,color:C.text3,background:C.bg,borderRadius:8,marginBottom:16,fontSize:13}}>
              No items linked yet. Click "Link Item" to add inventory items this vendor supplies.
            </div>
          ) : (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden',marginBottom:16}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:C.bg}}>
                    {['Item','Unit','Unit Price','Min Qty','Lead Time','Primary',''].map(h=>(
                      <th key={h} style={{padding:'8px 12px',textAlign:'left',color:C.text3,fontWeight:700}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(viewVendor.items||[]).map((item,i)=>(
                    <tr key={item.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.surface:'#F8FAF8'}}>
                      <td style={{padding:'8px 12px',fontWeight:600,color:C.text}}>{item.item_name}</td>
                      <td style={{padding:'8px 12px',color:C.text3}}>{item.unit}</td>
                      <td style={{padding:'8px 12px',color:C.accent,fontWeight:700}}>{inr(item.unit_price)}</td>
                      <td style={{padding:'8px 12px',color:C.text2}}>{item.min_order_qty}</td>
                      <td style={{padding:'8px 12px',color:C.text2}}>{item.lead_time_days} day(s)</td>
                      <td style={{padding:'8px 12px'}}>
                        {item.is_primary && <span style={{padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:700,background:C.accentLight,color:C.accent}}>Primary</span>}
                      </td>
                      <td style={{padding:'8px 12px'}}>
                        <button onClick={()=>removeVendorItem(item.id)}
                          style={{padding:'3px 8px',background:C.redBg,color:C.red,border:'none',borderRadius:6,fontSize:11,cursor:'pointer'}}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add Item Form */}
          {showAddItem && (
            <div style={{background:C.bg,borderRadius:10,padding:16,marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:C.text2,marginBottom:10,textTransform:'uppercase'}}>Link Inventory Item</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                <div>
                  {lbl('Select from Inventory')}
                  <select style={inp} value={itemForm.inventory_item_id}
                    onChange={e=>{
                      const item = inventory.find(i=>i.id===parseInt(e.target.value));
                      setItemForm(p=>({...p, inventory_item_id:e.target.value, item_name:item?.item_name||'', unit:item?.unit||'kg', unit_price:item?.last_purchase_price||item?.average_cost||''}));
                    }}>
                    <option value="">Select item...</option>
                    {inventory.map(i=><option key={i.id} value={i.id}>{i.item_name} ({i.unit})</option>)}
                  </select>
                </div>
                <div>{lbl('Unit Price (Rs)')}<input style={inp} type="number" value={itemForm.unit_price} onChange={e=>setItemForm(p=>({...p,unit_price:e.target.value}))} /></div>
                <div>{lbl('Min Order Qty')}<input style={inp} type="number" value={itemForm.min_order_qty} onChange={e=>setItemForm(p=>({...p,min_order_qty:e.target.value}))} /></div>
                <div>{lbl('Lead Time (Days)')}<input style={inp} type="number" value={itemForm.lead_time_days} onChange={e=>setItemForm(p=>({...p,lead_time_days:e.target.value}))} /></div>
              </div>
              <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,cursor:'pointer'}}>
                <input type="checkbox" checked={itemForm.is_primary} onChange={e=>setItemForm(p=>({...p,is_primary:e.target.checked}))} />
                <span style={{fontSize:13,color:C.text2,fontWeight:600}}>Set as Primary Vendor for this item</span>
              </label>
              <div style={{display:'flex',gap:8}}>
                <button onClick={addItemToVendor} disabled={saving}
                  style={{flex:1,padding:'8px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
                  {saving?'Saving...':'Link Item'}
                </button>
                <button onClick={()=>setShowAddItem(false)}
                  style={{flex:1,padding:'8px',background:'transparent',color:C.text2,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:'pointer'}}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Recent POs */}
          {(viewVendor.recent_pos||[]).length > 0 && (
            <div>
              <div style={{fontSize:13,fontWeight:700,color:C.text2,textTransform:'uppercase',marginBottom:10}}>Recent Purchase Orders</div>
              {viewVendor.recent_pos.map(po=>(
                <div key={po.id} style={{display:'flex',justifyContent:'space-between',padding:'10px 14px',background:C.bg,borderRadius:8,marginBottom:6}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{po.po_number}</div>
                    <div style={{fontSize:11,color:C.text3}}>{new Date(po.created_at).toLocaleDateString('en-IN')}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:14,fontWeight:700,color:C.accent}}>{inr(po.total_amount)}</div>
                    <span style={{fontSize:11,padding:'2px 8px',borderRadius:99,background:po.status==='Received'?C.accentLight:C.amberBg,color:po.status==='Received'?C.accent:C.amber,fontWeight:600}}>{po.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{padding:'24px 28px',background:C.bg,minHeight:'100vh'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.text}}>Vendor Management</div>
          <div style={{fontSize:13,color:C.text3,marginTop:4}}>{vendors.length} vendors · {vendors.filter(v=>v.status==='Active').length} active</div>
        </div>
        <div style={{display:'flex',gap:10}}>
          <button onClick={autoGenerateVendorPOs} disabled={generating}
            style={{padding:'9px 16px',background:C.amberBg,color:C.amber,border:`1px solid ${C.amberBorder}`,borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
            {generating?'Generating...':'Auto-Gen POs by Vendor'}
          </button>
          <button onClick={()=>{setShowAdd(true);setEditVendor(null);setForm(initForm);}}
            style={{padding:'9px 16px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
            + Add Vendor
          </button>
        </div>
      </div>

      {msg && <div style={{padding:'10px 16px',borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600,background:msg.includes('Error')||msg.includes('Failed')?C.redBg:C.accentLight,color:msg.includes('Error')||msg.includes('Failed')?C.red:C.accent,border:`1px solid ${msg.includes('Error')||msg.includes('Failed')?C.red:C.accent}`}}>{msg}</div>}

      {/* Tabs */}
      <div style={{display:'flex',gap:8,marginBottom:20}}>
        {[['vendors','Vendors'],['pos','Generated POs']].map(([id,label])=>(
          <button key={id} onClick={()=>setActiveTab(id)}
            style={{padding:'8px 16px',background:activeTab===id?C.accent:C.surface,color:activeTab===id?'#fff':C.text2,border:`1px solid ${activeTab===id?C.accent:C.border}`,borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer'}}>
            {label} {id==='pos'&&generatedPOs.length>0?`(${generatedPOs.length})`:''}
          </button>
        ))}
      </div>

      {/* Filters */}
      {activeTab==='vendors' && (
        <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search vendors..."
            style={{padding:'8px 14px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text,fontSize:13,width:220}} />
          <select value={filterCat} onChange={e=>setFilterCat(e.target.value)}
            style={{padding:'8px 12px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text,fontSize:13}}>
            <option value="">All Categories</option>
            {VENDOR_CATEGORIES.map(c=><option key={c}>{c}</option>)}
          </select>
          <button onClick={load} style={{padding:'8px 14px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text2,fontSize:13,cursor:'pointer'}}>Refresh</button>
        </div>
      )}

      {/* VENDORS LIST */}
      {activeTab==='vendors' && (
        loading ? <div style={{textAlign:'center',padding:60,color:C.text3}}>Loading vendors...</div> : (
          filtered.length===0 ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,color:C.text3,border:`1px solid ${C.border}`}}>
              No vendors yet. Click "+ Add Vendor" to get started.
            </div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:14}}>
              {filtered.map(vendor=>(
                <div key={vendor.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,borderLeft:`4px solid ${vendor.status==='Active'?C.accent:C.red}`}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                    <div>
                      <div style={{fontSize:15,fontWeight:800,color:C.text,marginBottom:2}}>{vendor.name}</div>
                      <div style={{fontSize:11,color:C.text3}}>{vendor.vendor_code} · {vendor.category}</div>
                    </div>
                    <span style={{padding:'3px 10px',borderRadius:99,fontSize:11,fontWeight:700,background:vendor.status==='Active'?C.accentLight:C.redBg,color:vendor.status==='Active'?C.accent:C.red}}>
                      {vendor.status}
                    </span>
                  </div>

                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12,fontSize:12}}>
                    <div><span style={{color:C.text3}}>Contact: </span><span style={{color:C.text,fontWeight:600}}>{vendor.contact_person||'—'}</span></div>
                    <div><span style={{color:C.text3}}>Phone: </span><span style={{color:C.text,fontWeight:600}}>{vendor.phone||'—'}</span></div>
                    <div><span style={{color:C.text3}}>Payment: </span><span style={{color:C.text,fontWeight:600}}>{vendor.payment_terms}</span></div>
                    <div><span style={{color:C.text3}}>Items: </span><span style={{color:C.accent,fontWeight:700}}>{vendor.item_count||0}</span></div>
                  </div>

                  <StarRating rating={vendor.rating||5} />

                  <div style={{display:'flex',gap:8,marginTop:12}}>
                    <button onClick={()=>openView(vendor)}
                      style={{flex:1,padding:'7px',background:C.accentLight,color:C.accent,border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>View / Link Items</button>
                    <button onClick={()=>openEdit(vendor)}
                      style={{flex:1,padding:'7px',background:C.bg,color:C.text2,border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer'}}>Edit</button>
                    <button onClick={()=>deleteVendor(vendor.id)}
                      style={{padding:'7px 10px',background:C.redBg,color:C.red,border:'none',borderRadius:8,fontSize:12,cursor:'pointer'}}>Del</button>
                  </div>
                </div>
              ))}
            </div>
          )
        )
      )}

      {/* GENERATED POs TAB */}
      {activeTab==='pos' && (
        <div>
          {generatedPOs.length===0 ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,color:C.text3,border:`1px solid ${C.border}`}}>
              No POs generated yet. Click "Auto-Gen POs by Vendor" to create vendor-specific POs for all low stock items.
            </div>
          ) : (
            generatedPOs.map(po=>(
              <div key={po.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,marginBottom:14}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                  <div>
                    <div style={{fontSize:16,fontWeight:800,color:C.text}}>{po.po_number}</div>
                    <div style={{fontSize:13,color:C.text2,marginTop:2}}>Vendor: <strong>{po.vendor_name}</strong></div>
                    <div style={{fontSize:11,color:C.text3,marginTop:2}}>{(po.items||[]).length} items</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:20,fontWeight:800,color:C.accent}}>{inr(po.total_amount)}</div>
                    <span style={{fontSize:11,padding:'2px 8px',borderRadius:99,background:C.amberBg,color:C.amber,fontWeight:700}}>Draft</span>
                  </div>
                </div>

                {/* Items */}
                <div style={{background:C.bg,borderRadius:8,padding:12,marginBottom:12}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead>
                      <tr>
                        {['Item','Current Stock','Reorder Level','Qty to Order','Unit Price','Total'].map(h=>(
                          <th key={h} style={{padding:'6px 10px',textAlign:'left',color:C.text3,fontWeight:700}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(po.items||[]).map((item,i)=>(
                        <tr key={i} style={{borderTop:`1px solid ${C.border}`}}>
                          <td style={{padding:'6px 10px',fontWeight:600,color:C.text}}>{item.item_name}</td>
                          <td style={{padding:'6px 10px',color:C.red,fontWeight:700}}>{item.current_stock} {item.unit}</td>
                          <td style={{padding:'6px 10px',color:C.text3}}>{item.reorder_level} {item.unit}</td>
                          <td style={{padding:'6px 10px',color:C.text,fontWeight:700}}>{item.quantity_ordered} {item.unit}</td>
                          <td style={{padding:'6px 10px',color:C.text2}}>{inr(item.unit_price)}</td>
                          <td style={{padding:'6px 10px',color:C.accent,fontWeight:700}}>{inr(item.total_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Send Options */}
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <div style={{fontSize:12,color:C.text3,alignSelf:'center'}}>Send to vendor via:</div>
                  <button onClick={()=>sendPO(po.id,'whatsapp')}
                    style={{padding:'7px 14px',background:'#25D366',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>
                    WhatsApp
                  </button>
                  <button onClick={()=>sendPO(po.id,'email')}
                    style={{padding:'7px 14px',background:C.blueBg,color:C.blue,border:`1px solid ${C.blue}`,borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>
                    Email
                  </button>
                  <button onClick={()=>window.print()}
                    style={{padding:'7px 14px',background:C.bg,color:C.text2,border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer'}}>
                    Print PDF
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showAdd && <VendorForm />}
      {viewVendor && <VendorDetail />}
    </div>
  );
}
